// Validation rules copied VERBATIM from the other TC Auditors sites so every site accepts and
// rejects exactly the same values. Keep in sync if the source rules change.
//
//  - validateTaxNumber, validateSAIdNumber, validateLuhnChecksum:
//      Personal-Tax-Services-Agreement/frontend/src/pages/PageOne.jsx (via SARS-Power-Of-Attorney)
//  - SOUTH_AFRICAN_BANKS (universal branch codes), BANK_ACCOUNT_RULES, formatPrefixList,
//    validateEmail, validatePhoneNumber, validateAccountNumber:
//      TCA-Debit-Order-Mandate/frontend/src/App.js (moved out of the component, unchanged)

export const validateTaxNumber = (taxNumber) => {
  if (!taxNumber || taxNumber.trim() === '') {
    return { valid: false, error: '' }; // Empty handled by required field
  }

  // Remove any spaces
  const cleanTax = taxNumber.replace(/\s/g, '');

  // Check: Must be exactly 10 digits, no special characters, no letters
  if (!/^\d{10}$/.test(cleanTax)) {
    if (/[a-zA-Z]/.test(taxNumber)) {
      return { 
        valid: false, 
        error: 'Tax number must not contain letters'
      };
    }
    if (/[^0-9\s]/.test(taxNumber)) {
      return { 
        valid: false, 
        error: 'Tax number must not contain special characters'
      };
    }
    if (cleanTax.length !== 10) {
      return { 
        valid: false, 
        error: `Tax number must be exactly 10 digits. Currently ${cleanTax.length} digits.`
      };
    }
  }

  // Check: Tax number cannot start with 4, 7, or 9
  const firstDigit = cleanTax[0];
  if (firstDigit === '4' || firstDigit === '7' || firstDigit === '9') {
    return { 
      valid: false, 
      error: 'Invalid tax number, please double check'
    };
  }

  // Check: Prevent all same digits (e.g., 1111111111, 0000000000)
  if (/^(\d)\1{9}$/.test(cleanTax)) {
    return { 
      valid: false, 
      error: 'Invalid tax number - cannot be all the same digit'
    };
  }

  // Check: Prevent sequential ascending numbers (e.g., 0123456789, 1234567890)
  const ascendingSequences = ['0123456789', '1234567890', '2345678901', '3456789012', '4567890123', '5678901234', '6789012345', '7890123456', '8901234567', '9012345678'];
  if (ascendingSequences.includes(cleanTax)) {
    return { 
      valid: false, 
      error: 'Invalid tax number - sequential numbers not allowed'
    };
  }

  // Check: Prevent sequential descending numbers (e.g., 9876543210, 0987654321)
  const descendingSequences = ['9876543210', '0987654321', '1098765432', '2109876543', '3210987654', '4321098765', '5432109876', '6543210987', '7654321098', '8765432109'];
  if (descendingSequences.includes(cleanTax)) {
    return { 
      valid: false, 
      error: 'Invalid tax number - sequential numbers not allowed'
    };
  }

  // Check: Prevent repeating patterns (e.g., 1212121212, 1231231231, 1234123412)
  // 2-digit repeating pattern
  if (/^(\d{2})\1{4}$/.test(cleanTax)) {
    return { 
      valid: false, 
      error: 'Invalid tax number - repeating pattern not allowed'
    };
  }
  // 5-digit repeating pattern
  if (/^(\d{5})\1$/.test(cleanTax)) {
    return { 
      valid: false, 
      error: 'Invalid tax number - repeating pattern not allowed'
    };
  }

  // Check: Prevent common bypass attempts
  const invalidNumbers = [
    '1234567890', '0987654321', '1111111111', '0000000000',
    '1234512345', '9999999999', '1010101010', '1122334455',
    '1212121212', '1231231231', '1234123412', '5555555555',
    '1357924680', '2468013579', '9876509876', '0123401234'
  ];
  if (invalidNumbers.includes(cleanTax)) {
    return { 
      valid: false, 
      error: 'Invalid tax number, please enter your actual tax number'
    };
  }

  // Check: Prevent any 2-digit pattern repeating 3 or more times
  const twoDigitPatterns = {};
  for (let i = 0; i <= cleanTax.length - 2; i++) {
    const pattern = cleanTax.substring(i, i + 2);
    twoDigitPatterns[pattern] = (twoDigitPatterns[pattern] || 0) + 1;
    
    if (twoDigitPatterns[pattern] >= 3) {
      return { 
        valid: false, 
        error: 'Invalid tax number - suspicious repeating pattern detected'
      };
    }
  }

  // Check: Prevent any 3-digit pattern repeating 3 or more times
  const threeDigitPatterns = {};
  for (let i = 0; i <= cleanTax.length - 3; i++) {
    const pattern = cleanTax.substring(i, i + 3);
    threeDigitPatterns[pattern] = (threeDigitPatterns[pattern] || 0) + 1;
    
    if (threeDigitPatterns[pattern] >= 3) {
      return { 
        valid: false, 
        error: 'Invalid tax number - suspicious repeating pattern detected'
      };
    }
  }

  // Check: Prevent any 4-digit pattern repeating 2 or more times
  const fourDigitPatterns = {};
  for (let i = 0; i <= cleanTax.length - 4; i++) {
    const pattern = cleanTax.substring(i, i + 4);
    fourDigitPatterns[pattern] = (fourDigitPatterns[pattern] || 0) + 1;
    
    if (fourDigitPatterns[pattern] >= 2) {
      return { 
        valid: false, 
        error: 'Invalid tax number - suspicious repeating pattern detected'
      };
    }
  }

  // Check: Prevent any 5-digit pattern repeating 2 or more times
  const fiveDigitPatterns = {};
  for (let i = 0; i <= cleanTax.length - 5; i++) {
    const pattern = cleanTax.substring(i, i + 5);
    fiveDigitPatterns[pattern] = (fiveDigitPatterns[pattern] || 0) + 1;
    
    if (fiveDigitPatterns[pattern] >= 2) {
      return { 
        valid: false, 
        error: 'Invalid tax number - suspicious repeating pattern detected'
      };
    }
  }

  // Check: Prevent 4+ consecutive ascending or descending digits
  for (let i = 0; i <= cleanTax.length - 4; i++) {
    let isAscending = true;
    let isDescending = true;
    
    // Check if next 4 digits are consecutive ascending
    for (let j = i; j < i + 3; j++) {
      const current = parseInt(cleanTax[j]);
      const next = parseInt(cleanTax[j + 1]);
      
      if (next !== current + 1) {
        isAscending = false;
      }
      if (next !== current - 1) {
        isDescending = false;
      }
    }
    
    if (isAscending || isDescending) {
      return { 
        valid: false, 
        error: 'Invalid tax number - sequential numbers not allowed'
      };
    }
  }

  return { valid: true, error: '' };
};

export const validateSAIdNumber = (idNumber) => {
  if (!idNumber || idNumber.trim() === '') {
    return { valid: false, error: '', info: null }; // Empty handled by required field
  }

  // Remove any spaces or dashes
  const cleanId = idNumber.replace(/[\s-]/g, '');

  // Check 1: Must be exactly 13 digits
  if (!/^\d{13}$/.test(cleanId)) {
    return { 
      valid: false, 
      error: 'ID number must be exactly 13 digits (no letters or special characters)',
      info: null 
    };
  }

  // Extract components
  const year = cleanId.substring(0, 2);
  const month = cleanId.substring(2, 4);
  const day = cleanId.substring(4, 6);
  const genderDigits = cleanId.substring(6, 10);
  const citizenshipDigit = cleanId.substring(10, 11);
  const raceDigit = cleanId.substring(11, 12);
  const checkDigit = cleanId.substring(12, 13);

  // Check 2: Validate date of birth (YYMMDD)
  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);
  const yearNum = parseInt(year, 10);

  // Month must be 01-12
  if (monthNum < 1 || monthNum > 12) {
    return { 
      valid: false, 
      error: `Invalid month in ID number: ${month}. Month must be between 01 and 12`,
      info: null 
    };
  }

  // Determine full year (assuming 00-current year+10 is 2000s, rest is 1900s)
  const currentYear = new Date().getFullYear();
  const currentYearLastTwo = currentYear % 100;
  const fullYear = yearNum <= currentYearLastTwo + 10 ? 2000 + yearNum : 1900 + yearNum;

  // Check if it's a leap year
  const isLeapYear = (fullYear % 4 === 0 && fullYear % 100 !== 0) || (fullYear % 400 === 0);

  // Days per month
  const daysInMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const maxDays = daysInMonth[monthNum - 1];

  if (dayNum < 1 || dayNum > maxDays) {
    return { 
      valid: false, 
      error: `Invalid day in ID number: ${day}. Day must be between 01 and ${maxDays} for month ${month}`,
      info: null 
    };
  }

  // Check if birth date is not in the future
  const birthDate = new Date(fullYear, monthNum - 1, dayNum);
  const today = new Date();
  if (birthDate > today) {
    return { 
      valid: false, 
      error: 'Date of birth cannot be in the future',
      info: null 
    };
  }

  // Check 3: Gender sequence (0000-4999 = Female, 5000-9999 = Male)
  const genderNum = parseInt(genderDigits, 10);
  const gender = genderNum < 5000 ? 'Female' : 'Male';

  // Check 4: Citizenship digit (0 = SA Citizen, 1 = Permanent Resident, 2 = Refugee)
  let citizenship = '';
  if (citizenshipDigit === '0') {
    citizenship = 'South African Citizen';
  } else if (citizenshipDigit === '1') {
    citizenship = 'Permanent Resident';
  } else if (citizenshipDigit === '2') {
    citizenship = 'Refugee';
  } else {
    return { 
      valid: false, 
      error: `Invalid citizenship digit: ${citizenshipDigit}. Must be 0 (SA Citizen), 1 (Permanent Resident), or 2 (Refugee)`,
      info: null 
    };
  }

  // Check 5: Race/obsolete digit (should be 8 or 9 in modern IDs)
  // Note: We don't reject based on this as older IDs may have different values
  const isModernId = raceDigit === '8' || raceDigit === '9';

  // Check 6: Luhn Algorithm (Modulus 10) for check digit validation
  const luhnValid = validateLuhnChecksum(cleanId);
  if (!luhnValid) {
    return { 
      valid: false, 
      error: 'Invalid ID number: Check digit validation failed',
      info: null 
    };
  }

  // Calculate age
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  // All validations passed
  return {
    valid: true,
    error: '',
    info: {
      dateOfBirth: birthDate.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' }),
      age: age,
      gender: gender,
      citizenship: citizenship,
      isModernId: isModernId
    }
  };
};

// Luhn Algorithm implementation
export const validateLuhnChecksum = (idNumber) => {
  let sum = 0;
  let isSecond = false;

  // Process from right to left
  for (let i = idNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(idNumber[i], 10);

    if (isSecond) {
      digit *= 2;
      if (digit > 9) {
        digit = Math.floor(digit / 10) + (digit % 10);
      }
    }

    sum += digit;
    isSecond = !isSecond;
  }

  return sum % 10 === 0;
};

// List of South African Banks with Universal Branch Codes
export const SOUTH_AFRICAN_BANKS = [
  { value: 'absa', label: 'ABSA Bank', branchCode: '632005' },
  { value: 'african-bank', label: 'African Bank', branchCode: '430000' },
  { value: 'barclays-bank', label: 'Barclays Bank', branchCode: '590000' },
  { value: 'bidvest-bank', label: 'Bidvest Bank', branchCode: '462005' },
  { value: 'capitec', label: 'Capitec Bank', branchCode: '470010' },
  { value: 'capitec-business', label: 'Capitec Business Bank', branchCode: '450105' },
  { value: 'discovery-bank', label: 'Discovery Bank', branchCode: '679000' },
  { value: 'firstrand-bank', label: 'FirstRand Bank Ltd', branchCode: '201419' },
  { value: 'fnb', label: 'First National Bank (FNB)', branchCode: '250655' },
  { value: 'grindrod-bank', label: 'Grindrod Bank', branchCode: '584000' },
  { value: 'hollard-bank', label: 'Hollard Bank', branchCode: '585001' },
  { value: 'hsbc-bank', label: 'HSBC Bank', branchCode: '587000' },
  { value: 'investec', label: 'Investec Bank', branchCode: '580105' },
  { value: 'mercantile-bank', label: 'Mercantile Bank (now Capitec Business Bank)', branchCode: '450105' },
  { value: 'nedbank', label: 'Nedbank', branchCode: '198765' },
  { value: 'old-mutual-bank', label: 'Old Mutual Bank', branchCode: '462005' },
  { value: 'standard-bank', label: 'Standard Bank', branchCode: '051001' },
  { value: 'standard-chartered', label: 'Standard Chartered Bank', branchCode: '730020' },
  { value: 'tymebank', label: 'TymeBank', branchCode: '678910' },
  { value: 'bank-zero', label: 'Bank Zero', branchCode: '888000' },
  { value: 'sasfin-bank', label: 'Sasfin Bank', branchCode: '683000' },
  { value: 'bank-of-athens', label: 'South African Bank of Athens Limited', branchCode: '410506' },
  { value: 'albaraka-bank', label: 'Albaraka Bank', branchCode: '800000' },
  { value: 'habib-bank', label: 'Habib Overseas Bank', branchCode: '170102' },
  { value: 'hbz-bank', label: 'HBZ Bank', branchCode: '570105' },
  { value: 'ithala-bank', label: 'Ithala Bank', branchCode: '420000' },
  { value: 'postbank', label: 'PostBank (South African Post Office)', branchCode: '460005' },
  { value: 'rand-merchant-bank', label: 'Rand Merchant Bank', branchCode: '261251' },
  { value: 'rmbpb', label: 'RMB Private Bank', branchCode: '222026' },
  { value: 'ubank', label: 'Ubank', branchCode: '431010' },
].sort((a, b) => a.label.localeCompare(b.label));

// Per-bank account number validation rules (keyed by exact bank label above)
export const BANK_ACCOUNT_RULES = {
  'ABSA Bank': { minLength: 10, maxLength: 11, prefixes: ['1', '4', '9'], prefixRequired: false, name: 'ABSA Bank', description: '10 or 11 digits', note: 'Accounts are left-padded with zeros to 11 digits for processing.' },
  'First National Bank (FNB)': { minLength: 11, maxLength: 11, prefixes: ['5', '6', '7'], prefixRequired: true, name: 'FNB', description: '11 digits', note: 'Cheque accounts start with 6; Savings with 7 or 5.' },
  'Nedbank': { minLength: 10, maxLength: 10, prefixes: ['1'], prefixRequired: true, name: 'Nedbank', description: '10 digits', note: 'Please enter your Account Number, not your Profile Number.' },
  'Standard Bank': { minLength: 9, maxLength: 11, prefixes: ['0', '1', '2', '3', '4'], prefixRequired: false, name: 'Standard Bank', description: '9 to 11 digits', note: '9-digit accounts are valid and will be left-padded with zeros.' },
  'Capitec Bank': { minLength: 10, maxLength: 10, prefixes: ['1', '2'], prefixRequired: true, name: 'Capitec Bank', description: '10 digits', note: 'Savings accounts start with 1; Loan/Bond accounts start with 2.' },
  'Capitec Business Bank': { minLength: 10, maxLength: 10, prefixes: ['1', '2'], prefixRequired: false, name: 'Capitec Business Bank', description: '10 digits' },
  'African Bank': { minLength: 11, maxLength: 11, name: 'African Bank', description: '11 digits' },
  'Ubank': { minLength: 11, maxLength: 11, name: 'Ubank', description: '11 digits', note: 'Formerly Teba Bank.' },
  'Discovery Bank': { minLength: 11, maxLength: 11, rejectLength: 16, name: 'Discovery Bank', description: '11 digits', note: 'Please enter your account number, not your 16-digit card number.' },
  'TymeBank': { minLength: 11, maxLength: 11, name: 'TymeBank', description: '11 digits' },
  'Bank Zero': { minLength: 10, maxLength: 11, name: 'Bank Zero', description: '10 or 11 digits' },
  'Bidvest Bank': { minLength: 11, maxLength: 11, name: 'Bidvest Bank', description: '11 digits' },
  'Sasfin Bank': { minLength: 11, maxLength: 11, name: 'Sasfin Bank', description: '11 digits' },
  'Investec Bank': { minLength: 11, maxLength: 11, prefixes: ['1', '5'], prefixRequired: false, name: 'Investec Bank', description: '11 digits', note: 'Private Bank accounts often start with 1; Corporate with 5.' },
  'South African Bank of Athens Limited': { minLength: 11, maxLength: 11, name: 'Bank of Athens', description: '11 digits', note: 'Formerly Access Bank South Africa.' },
  'Albaraka Bank': { minLength: 11, maxLength: 11, name: 'Albaraka Bank', description: '11 digits', note: 'Islamic Banking. Do not enter international IBAN format.' },
  'Grindrod Bank': { minLength: 11, maxLength: 11, name: 'Grindrod Bank', description: '11 digits' },
  'Habib Overseas Bank': { minLength: 11, maxLength: 11, name: 'Habib Overseas Bank', description: '11 digits' },
  'HBZ Bank': { minLength: 11, maxLength: 11, name: 'HBZ Bank', description: '11 digits' },
  'Ithala Bank': { minLength: 11, maxLength: 11, name: 'Ithala Bank', description: '11 digits' },
  'Mercantile Bank (now Capitec Business Bank)': { minLength: 11, maxLength: 11, name: 'Mercantile Bank', description: '11 digits' },
  'PostBank (South African Post Office)': { minLength: 11, maxLength: 11, name: 'PostBank', description: '11 digits' },
  'Rand Merchant Bank': { minLength: 11, maxLength: 11, prefixes: ['5', '6', '7'], prefixRequired: false, name: 'Rand Merchant Bank', description: '11 digits', note: 'Uses same validation rules as FNB.' },
  'RMB Private Bank': { minLength: 11, maxLength: 11, prefixes: ['5', '6', '7'], prefixRequired: false, name: 'RMB Private Bank', description: '11 digits', note: 'Uses same validation rules as FNB.' },
  'Barclays Bank': { minLength: 10, maxLength: 11, name: 'Barclays Bank', description: '10 or 11 digits' },
  'FirstRand Bank Ltd': { minLength: 11, maxLength: 11, prefixes: ['5', '6', '7'], prefixRequired: false, name: 'FirstRand', description: '11 digits', note: 'Parent company of FNB - uses same validation rules.' },
  'Hollard Bank': { minLength: 10, maxLength: 11, name: 'Hollard Bank', description: '10 or 11 digits' },
  'HSBC Bank': { minLength: 10, maxLength: 11, name: 'HSBC Bank', description: '10 or 11 digits' },
  'Old Mutual Bank': { minLength: 10, maxLength: 11, name: 'Old Mutual Bank', description: '10 or 11 digits' },
  'Standard Chartered Bank': { minLength: 10, maxLength: 11, name: 'Standard Chartered Bank', description: '10 or 11 digits' },
};

// Format a list of prefixes as "5, 6, or 7"
export const formatPrefixList = (prefixes) =>
  prefixes.length > 1
    ? `${prefixes.slice(0, -1).join(', ')}, or ${prefixes[prefixes.length - 1]}`
    : prefixes[0];

export const validateEmail = (value) => {
  if (!value) {
    return '';
  }
  
  const email = value.trim().toLowerCase();
  
  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return 'Please enter a valid email address';
  }
  
  // Common typo domains
  const commonDomains = {
    'gmial.com': 'gmail.com',
    'gmai.com': 'gmail.com',
    'gmail.co': 'gmail.com',
    'gnail.com': 'gmail.com',
    'hotmial.com': 'hotmail.com',
    'hotmail.co': 'hotmail.com',
    'yahooo.com': 'yahoo.com',
    'yaho.com': 'yahoo.com',
    'yahoo.co': 'yahoo.com',
    'outlok.com': 'outlook.com',
    'outllook.com': 'outlook.com',
    'outlook.co': 'outlook.com',
  };
  
  const domain = email.split('@')[1];
  if (commonDomains[domain]) {
    return `Did you mean ${email.split('@')[0]}@${commonDomains[domain]}?`;
  }
  
  return '';
};

export const validatePhoneNumber = (value, fieldLabel = 'Contact number') => {
  // Remove any spaces or dashes for validation
  const cleanedValue = value.replace(/[\s-]+/g, '');

  if (!cleanedValue) {
    return '';
  }

  if (!/^\d+$/.test(cleanedValue)) {
    return `${fieldLabel} must contain only digits`;
  }

  if (!cleanedValue.startsWith('0')) {
    return `${fieldLabel} must start with 0`;
  }

  if (cleanedValue.length !== 10) {
    return `${fieldLabel} must be exactly 10 digits`;
  }

  return '';
};

export const validateAccountNumber = (accountNumber, bankName) => {
  if (!accountNumber || !bankName) {
    return '';
  }
  
  // Remove any spaces or non-numeric characters
  const cleanNumber = accountNumber.replace(/\D/g, '');
  
  // === PATTERN VALIDATION CHECKS (DETECT SUSPICIOUS NUMBERS) ===
  
  // 1. Check for all same digits (e.g., 1111111111, 9999999999)
  if (/^(.)\1+$/.test(cleanNumber)) {
    return 'Account number appears invalid. Please enter a valid bank account number.';
  }
  
  // 2. Check for all zeros
  if (/^0+$/.test(cleanNumber)) {
    return 'Account number appears invalid. Please enter a valid bank account number.';
  }
  
  // 3. Check for sequential ascending numbers (e.g., 123456789, 234567890)
  const hasAscendingSequence = (str) => {
    for (let i = 0; i < str.length - 5; i++) {
      let isSequential = true;
      for (let j = 0; j < 6; j++) {
        if (parseInt(str[i + j]) !== (parseInt(str[i]) + j) % 10) {
          isSequential = false;
          break;
        }
      }
      if (isSequential) return true;
    }
    return false;
  };
  
  if (hasAscendingSequence(cleanNumber)) {
    return 'Account number appears invalid. Sequential numbers are not permitted.';
  }
  
  // 4. Check for sequential descending numbers (e.g., 987654321)
  const hasDescendingSequence = (str) => {
    for (let i = 0; i < str.length - 5; i++) {
      let isSequential = true;
      for (let j = 0; j < 6; j++) {
        const expected = (parseInt(str[i]) - j + 10) % 10;
        if (parseInt(str[i + j]) !== expected) {
          isSequential = false;
          break;
        }
      }
      if (isSequential) return true;
    }
    return false;
  };
  
  if (hasDescendingSequence(cleanNumber)) {
    return 'Account number appears invalid. Sequential numbers are not permitted.';
  }
  
  // 5. Check for four or more consecutive identical digits (e.g., 1234444567)
  if (/(\d)\1{3,}/.test(cleanNumber)) {
    return 'Account number appears unusual. Contains four or more consecutive identical digits. Please verify before proceeding.';
  }
  
  // 6. Check for simple alternating patterns (e.g., 121212, 343434)
  const hasAlternatingPattern = (str) => {
    if (str.length < 6) return false;
    const pattern = str.substring(0, 2);
    const repeated = pattern.repeat(Math.ceil(str.length / 2)).substring(0, str.length);
    return str === repeated;
  };
  
  if (hasAlternatingPattern(cleanNumber)) {
    return 'Account number appears invalid. Repetitive patterns are not permitted.';
  }
  
  // 7. Check for insufficient digit variety (at least 3 different digits required)
  const uniqueDigits = new Set(cleanNumber.split('')).size;
  if (uniqueDigits < 3) {
    return 'Account number appears invalid. Please enter a valid bank account number with varied digits.';
  }
  
  // === BANK-SPECIFIC VALIDATION (length, prefix, special rejections) ===
  const validation = BANK_ACCOUNT_RULES[bankName];

  if (validation) {
    // Special check: reject 16-digit card numbers (e.g. Discovery Bank)
    if (validation.rejectLength && cleanNumber.length === validation.rejectLength) {
      return `Please enter your ${validation.name} account number, not your 16-digit card number.`;
    }

    // Length validation
    if (validation.minLength === validation.maxLength) {
      if (cleanNumber.length !== validation.minLength) {
        return `${validation.name} account numbers must be exactly ${validation.minLength} digits.`;
      }
    } else if (cleanNumber.length < validation.minLength || cleanNumber.length > validation.maxLength) {
      return `${validation.name} account numbers must be between ${validation.minLength} and ${validation.maxLength} digits.`;
    }

    // Prefix validation
    if (validation.prefixes) {
      const firstDigit = cleanNumber.charAt(0);
      if (!validation.prefixes.includes(firstDigit)) {
        const prefixText = formatPrefixList(validation.prefixes);
        if (validation.prefixRequired) {
          return `${validation.name} account numbers must start with ${prefixText}.`;
        }
        return `${validation.name} account numbers typically start with ${prefixText}. Please verify your number is correct before proceeding.`;
      }
    }
  } else {
    // Fallback for any bank not in the rules map: SA standard range is 9-11 digits
    if (cleanNumber.length < 9 || cleanNumber.length > 11) {
      return `South African bank account numbers are typically 9-11 digits. You entered ${cleanNumber.length}. Please verify your number is correct.`;
    }
  }

  return '';
};

